<template>
    <!-- <div> -->
    <div v-if="showUsersList"
        ref="mentionListRef"
        class="position-ab overflow-y-auto bg-white style-scroll border-radius-5-px z-index-6 show__userlist">
        <ul class="d-flex flex-column m-0 p-0">
            <template v-if="filteredUsers.length">
                <li class="d-flex align-items-center cursor-pointer p5px-p10px"
                    :class="{ 'bg-blue white': selectedUserIndex === index }" v-for="(data, index) in filteredUsers"
                    :key="index" :data-mention-active="selectedUserIndex === index"
                    @mouseover="selectedUserIndex = index" @click="addMention(data)">
                    <UserProfile :showDot="false" class="user__profile cursor-pointer mr-10px" :data="{
                        id: data.key,
                        image: data.image,
                        title: data.name
                    }" width="30px" :thumbnail="'30x30'" />
                    <span>{{ data.name }}</span>
                </li>
            </template>
            <template v-else>
                <li class="d-flex align-items-center cursor-pointer p5px-p10px" @click="focusTextArea()">
                    {{ $t('Comments.no_user_found') }}
                </li>
            </template>
        </ul>
    </div>
    <div class="d-flex flex-column w-100">
        <div v-if="reply && Object.keys(reply).length" id="reply-box"
            class="d-flex align-items-center justify-content-between overflow-y-auto bg-white style-scroll reply-box">
            <div class="d-flex align-items-center emp__profile-wrapper">
                <img v-if="getReplyIcon(reply)" :src="getReplyIcon(reply)" class="reply-file-icon mr-10px" width="25" height="25" />
                <div class="d-flex flex-column">
                    <div class="reply-box-name text-nowrap cursor-default mr-5px">{{ $t('ContactInfo.reply_to', 'Reply to') }} {{ getUser(reply.userId).Employee_Name }}</div>
                    <span v-if="reply.type === 'text' || reply.type === 'link'" class="text-ellipsis cursor-default reply-box-content"
                        :title="checkLink(changeText(reply.message), true)"
                        v-html="checkLink(changeText(reply.message), true)"></span>
                    <span v-else class="text-ellipsis cursor-default" :title="getReplyLabel(reply.type)">{{ getReplyLabel(reply.type) }}</span>
                </div>
            </div>
            <div class="d-flex align-items-center">
                <img :src="closeIcon" @click="$emit('cancel-reply')" alt="closeIcon"
                    class="cursor-pointer cancel__reply">
            </div>
        </div>

        <textarea 
            v-show="!recording"
            type="text"
            class="write-message style-scroll"
            id="message-box"
            ref="messageBox"
            :placeholder="$t('ChatDetail.type_a_message', 'Type a message')"
            @keydown.enter="handleEnter"
            @keydown="keyDown"
            @mouseup="keyUp"
            @keyup="keyUp"
            @paste="handlePaste"
            :value="modelValue"
            :class="{
                'border-bottom-radius-5-px': reply,
                'border-radius-5-px': !Object.keys(reply).length,
                'disabled': !sendMessageAllowed,
                'placeholder-center': !modelValue || !modelValue.length
            }"
            @input="onInput"
            :disabled="!sendMessageAllowed"
        ></textarea>
    </div>
    <!-- </div> -->
</template>

<script setup>
// PACKAGES
import { i18n } from "@/locales/main";

import { useCustomComposable, useGetterFunctions } from "@/composable";
import { defineProps, defineEmits, computed, onMounted, watch, ref, nextTick, inject } from "vue";

// COMPONENTS
import UserProfile from "@/components/atom/UserProfile/UserProfile.vue"

// UTILS
const { getUser } = useGetterFunctions();
const { changeText, checkLink } = useCustomComposable();

const getReplyIcon = (reply) => {
    if (['text', 'link', 'sticker', 'image'].includes(reply.type)) return null;
    
    const fileName = reply.mediaOriginalName || reply.mediaName || "";
    let ext = fileName.split('.').pop()?.toLowerCase();
    if (!ext || ext === fileName.toLowerCase()) {
        ext = reply.type;
    }
    
    switch (ext) {
        case 'pdf': return require("@/assets/images/pdf.png");
        case 'xlsx':
        case 'xls':
        case 'excel': return require("@/assets/images/xls.png");
        case 'docx':
        case 'doc':
        case 'word': return require("@/assets/images/doc.png");
        case 'pptx':
        case 'ppt': return require("@/assets/images/pdf.png");
        case 'zip':
        case 'rar': return require("@/assets/images/zip.png");
        case 'mp4':
        case 'mov': return require("@/assets/images/mp4.png");
        case 'mp3':
        case 'wav': return require("@/assets/images/mp3.png");
        case 'txt': return require("@/assets/images/txt.png");
        default: return require("@/assets/images/svg/file_gray.svg");
    }
};

const getReplyLabel = (type) => {
    if (type === 'sticker') return i18n.global.t('Common.sticker', 'Sticker');
    if (props.reply?.mediaOriginalName || props.reply?.mediaName) return props.reply.mediaOriginalName || props.reply.mediaName;
    switch (type) {
        case 'image': return i18n.global.t('Common.image', 'Image');
        case 'video': return i18n.global.t('Common.video', 'Video');
        case 'audio': return i18n.global.t('Common.audio_message', 'Audio');
        case 'pdf': return i18n.global.t('Common.pdf', 'PDF');
        case 'excel': return i18n.global.t('Common.spreadsheet', 'Spreadsheet');
        default: return i18n.global.t('Common.attachment', 'Attachment');
    }
};



// IMAGES
const closeIcon = require("@/assets/images/svg/cancel_reply_icon.svg");
const defaultProfile = inject("$defaultUserAvatar");
const clientWidth = inject("$clientWidth");

const props = defineProps({
    recording: {
        type: Boolean,
        default: false
    },
    modelValue: {
        type: String,
        default: ""
    },
    backendValue: {
        type: String,
        default: ""
    },
    reply: {
        type: Object,
        default: null
    },
    userIds: {
        type: Array,
        default: () => []
    },
    showAll: {
        type: Boolean,
        default: false
    },
    sendMessageAllowed: {
        type: Boolean,
        default: true
    },
    isHeight: {
        type: Number,
        default: 24
    },
    loadingChat: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(["update:modelValue", "update:backendValue", "enter", "cancel-reply", "pasteFile"]);

const messageBox = ref("");
const mentionListRef = ref(null);
const showUsersList = ref(false);
const selectedUserIndex = ref(0);
const selectionIndex = ref(0);
const mentionSearch = ref("");
const users = ref([]);
const localBackend = ref(props.backendValue || "");

function setMentionUsers(userIds = props.userIds) {
    users.value = (userIds || []).map((x) => {
        const user = getUser(x);
        return {
            name: user.Employee_Name,
            image: user.Employee_profileImageURL,
            key: user.id,
            ghost: user.ghostUser
        }
    });

    if (props.showAll) {
        users.value.unshift({
            name: "All",
            image: defaultProfile.value,
            key: "all",
            ghost: false
        });
    }
}

onMounted(() => {
    setMentionUsers();

    // nextTick(() => {
    //     autoResize()
    // })
})

watch(() => props.loadingChat, (val) => {
    if (val === false) {
        nextTick(() => {
            autoResize();
        })
    }
});

watch(() => [props.userIds, props.showAll], () => setMentionUsers(), { deep: true });

const filteredUsers = computed(() => {
    return users.value.filter((x) => !x.ghost && x.name.replaceAll(" ", "").toLowerCase().includes(mentionSearch.value.toLowerCase()))
});
function handleEnter(e) {
    if (e.keyCode === 13 && !e.shiftKey) {
        if (clientWidth && clientWidth.value < 768) return;
        e.preventDefault();

        const selectedMention = filteredUsers.value[selectedUserIndex.value];
        if (showUsersList.value && selectedMention) {
            addMention(selectedMention);
            return;
        }

        // const textToSend = props.modelValue.trim();
        // if (!textToSend.length) return;

        const backendToSend = buildBackendFromUI(props.modelValue);
        emit("update:backendValue", backendToSend);
        emit("update:modelValue", backendToSend);
        emit("enter");
        emit("update:modelValue", "");
        localBackend.value = "";
        emit("update:backendValue", "");
    }
}

function onInput(e) {
    const val = e.target.value;
    emit("update:modelValue", val);
    nextTick(() => {
        autoResize();
    });
}

// SHOW MENTION USER LIST
function keyDown(e) {
    if (showUsersList.value) {
        if (e.keyCode === 38 || e.keyCode === 40) {
            e.preventDefault();
            if (e.keyCode === 40) {
                selectedUserIndex.value += selectedUserIndex.value < (filteredUsers.value.length - 1) ? 1 : 0;
            } else if (e.keyCode === 38) {
                selectedUserIndex.value -= selectedUserIndex.value > 0 ? 1 : 0;
            }
            nextTick(() => {
                scrollActiveMentionIntoView();
            });
        }
    }
}

function scrollActiveMentionIntoView() {
    const listEl = mentionListRef.value;
    const activeEl = listEl?.querySelector("[data-mention-active='true']");

    if (!listEl || !activeEl) {
        return;
    }

    activeEl.scrollIntoView({
        block: "nearest",
    });
}

//RESET MENTIONS
function resetMentions(reset = true) {
    if (!reset) {
        return;
    }

    selectedUserIndex.value = 0;
    showUsersList.value = false;
    selectionIndex.value = null;
    mentionSearch.value = "";
}

// ADD MENTION
function addMention(data = {}) {
    if (!Object.keys(data).length) {
        resetMentions();
        return;
    }

    const cursor = messageBox.value.selectionStart;

    let start = cursor - 1;
    while (start >= 0 && props.modelValue[start] !== "@") start--;
    if (start < 0) return;

    const before = props.modelValue.slice(0, start);
    const after = props.modelValue.slice(cursor);

    const newUi = before + "@" + data.name + " " + after;

    emit("update:modelValue", newUi);

    nextTick(() => {
        messageBox.value.focus();
        messageBox.value.selectionStart =
            messageBox.value.selectionEnd =
            before.length + data.name.length + 2;
        resetMentions();
    });
}

// --- helpers ---
// function backendToUi(text = "") {
//     // @[Name](id)  -> @Name
//     return text.replace(/@\[(.*?)\]\(.*?\)/g, "@$1");
// }

function buildBackendFromUI(uiText = "") {
    let result = "";
    let i = 0;

    while (i < uiText.length) {
        if (uiText[i] === "@") {
            const matchUser = users.value.find(u =>
                uiText.slice(i + 1).toLowerCase().startsWith(u.name.toLowerCase())
            );

            if (matchUser) {
                result += `@[${matchUser.name}](${matchUser.key})`;
                i += matchUser.name.length + 1; // +1 vì @
                continue;
            } else {
                result += "@";
                i++;
                continue;
            }
        } else {
            result += uiText[i];
            i++;
        }
    }

    return result;
}

function autoResize() {
    setTimeout(() => {
        const defaultTextHeight = 44;
        const textarea = messageBox.value;

        if (!textarea) {
            return;
        }

        const isConversationFooter = !!textarea.closest(".conversation-footer");
        const maxTextHeight = isConversationFooter ? 120 : 250;

        // reset height
        textarea.style.height = defaultTextHeight + "px";

        const scrollHeight = textarea.scrollHeight;
        const newHeight = Math.min(scrollHeight, maxTextHeight);
        textarea.style.height = newHeight + "px";
        textarea.style.overflowY = scrollHeight > maxTextHeight ? "auto" : "hidden";

        if (isConversationFooter) {
            return;
        }

        nextTick(() => {
            const creator_div = document.getElementById("creator_div");
            const reply_div = document.getElementById("reply_box") || 0;
            const message_container = document.getElementById("message_container");
            const creatorHeight = creator_div?.clientHeight;
            // const creatorOffsetHeight = creator_div?.offsetHeight;

            const comment_footer = newHeight + props.isHeight;
            const footerMin = Math.min(comment_footer, 275);

            const height =
                (creator_div ? (creatorHeight || 44) : 0) + footerMin + reply_div;

            if (!message_container) {
                return;
            }
            message_container.style.height = `calc(100% - ${height}px)`;
        });
    });
}


// CHECK MENTIONS
function keyUp(e) {
    // RESIZE TEXT AREA
    nextTick(() => {
        autoResize();
    })

    // check Escape
    if (e.keyCode === 27) {
        e.preventDefault();
        resetMentions();
        return;
    }

    // Check Mention
    let ind = messageBox.value.selectionStart - 1;
    checkShowMentions(ind, e.keyCode);
}

// CHECK TO SHOW MENTIONS
function checkShowMentions(selectionStart, keyCode) {
    if (!props.modelValue || !props.modelValue.length) {
        resetMentions();
        return;
    }
    let selectionIn = selectionStart;
    
    while (selectionIn >= 0) {
        let value = props.modelValue[selectionIn];
        if (value === "@") {
            selectionIndex.value = selectionIn + 1;
            showUsersList.value = true;
            mentionSearch.value = props.modelValue.substr(selectionIndex.value, (selectionStart + 1) - selectionIndex.value)
            break;
        } else if (value === " " || value === "\n") {
            resetMentions(keyCode !== 38 && keyCode !== 40);
            break;
        }
        selectionIn--;
    }

    if (selectionIn < 0) {
        resetMentions(keyCode !== 38 && keyCode !== 40);
    }
}

function getClipboardFiles(clipboardData) {
    const files = [];

    Array.from(clipboardData.files || []).forEach((file) => {
        if (file) files.push(file);
    });

    Array.from(clipboardData.items || []).forEach((item) => {
        if (item.kind !== "file") return;
        const file = item.getAsFile();
        if (file) files.push(file);
    });

    const fileMap = new Map();
    files.forEach((file) => {
        const isImage = file.type?.startsWith("image/");
        const key = isImage
            ? `${file.type}_${file.size}`
            : `${file.name}_${file.type}_${file.size}_${file.lastModified}`;

        if (!fileMap.has(key)) {
            fileMap.set(key, file);
        }
    });

    return Array.from(fileMap.values());
}

function decodeMessageEntities(value = "") {
    return String(value || "")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&amp;/g, "&");
}

function insertTextAtSelection(text = "") {
    const textarea = messageBox.value;
    if (!textarea) return;

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const currentValue = props.modelValue || "";
    const nextValue = `${currentValue.slice(0, start)}${text}${currentValue.slice(end)}`;

    emit("update:modelValue", nextValue);
    resetMentions();

    nextTick(() => {
        const cursor = start + text.length;
        textarea.focus();
        textarea.selectionStart = cursor;
        textarea.selectionEnd = cursor;
        autoResize();
    });
}

function getImageSourceFromHtml(html = "") {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.querySelector("img")?.getAttribute("src") || "";
}

function getExtensionFromMime(mime = "") {
    return mime.split("/")[1]?.split(";")[0] || "png";
}

async function imageSourceToFile(src = "") {
    if (!src) return null;

    const response = await fetch(src);
    const blob = await response.blob();
    if (!blob.type?.startsWith("image/")) return null;

    return new File(
        [blob],
        `pasted-image-${Date.now()}.${getExtensionFromMime(blob.type)}`,
        { type: blob.type }
    );
}

async function handlePaste(e) {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const files = getClipboardFiles(clipboardData);
    if (files.length) {
        e.preventDefault();
        emit("pasteFile", files);
        return;
    }

    const plainText = clipboardData.getData("text/plain");
    if (plainText) {
        e.preventDefault();
        insertTextAtSelection(decodeMessageEntities(plainText));
        return;
    }

    const imageSrc = getImageSourceFromHtml(clipboardData.getData("text/html"));
    if (!imageSrc) return;

    e.preventDefault();
    try {
        const file = await imageSourceToFile(imageSrc);
        if (file) emit("pasteFile", [file]);
    } catch (error) {
        console.error("ERROR in paste image:", error);
    }
}
</script>

<style>
@import './style.css';
.reply-file-icon {
    width: 25px;
    height: 25px;
    object-fit: contain;
    flex-shrink: 0;
}
</style>
